import type { Activity, ActivityChannel, ActivityDirection, Vendor, VendorDetail } from "../types";
import { formatDateTime } from "./format";

export type AccountTimelineKind =
  | "activity"
  | "order"
  | "follow_up"
  | "opportunity"
  | "sample"
  | "quote";

export interface AccountTimelineItem {
  id: string;
  kind: AccountTimelineKind;
  occurredAt: string;
  title: string;
  detail: string | null;
  status: string;
  channel: ActivityChannel | "commerce" | "task" | "pipeline";
  direction: ActivityDirection;
  actorName: string | null;
}

export class DatabaseConflictError extends Error {
  constructor(message = "This record changed on another device. Reload before saving.") {
    super(message);
    this.name = "DatabaseConflictError";
  }
}

export function activityChannel(
  activity: Pick<Activity, "activity_type" | "channel">,
): ActivityChannel {
  if (activity.channel) return activity.channel;
  switch (activity.activity_type) {
    case "visit":
      return "in_person";
    case "call":
      return "phone";
    case "email":
      return "email";
    default:
      return "other";
  }
}

export function vendorCrmDefaults(vendor: Vendor) {
  return {
    entity_type: vendor.entity_type ?? "venue",
    parent_vendor_id: vendor.parent_vendor_id ?? null,
    lifecycle_stage:
      vendor.lifecycle_stage ?? (vendor.status === "active" ? "customer" : "prospect"),
    account_priority: vendor.account_priority ?? "normal",
    contact_cadence_days: vendor.contact_cadence_days ?? null,
    next_contact_at: vendor.next_contact_at ?? null,
    owner_id: vendor.owner_id ?? null,
    row_version: vendor.row_version ?? 1,
  } as const;
}

export function buildAccountTimeline(detail: VendorDetail): AccountTimelineItem[] {
  const activities: AccountTimelineItem[] = detail.activities.map((activity) => ({
    id: activity.id,
    kind: "activity",
    occurredAt: activity.occurred_at,
    title: activity.summary,
    detail:
      [
        activity.details,
        activity.outcome ? `Outcome: ${activity.outcome}` : null,
        activity.next_action ? `Next: ${activity.next_action}` : null,
      ]
        .filter((value): value is string => Boolean(value))
        .join(" · ") || null,
    status: activity.activity_type,
    channel: activityChannel(activity),
    direction: activity.direction ?? "not_applicable",
    actorName: activity.author.display_name,
  }));
  const orders: AccountTimelineItem[] = detail.orders.map((order) => ({
    id: order.id,
    kind: "order",
    occurredAt: order.updated_at ?? order.created_at,
    title: order.description,
    detail: order.order_reference,
    status: order.status,
    channel: "commerce",
    direction: "inbound",
    actorName: null,
  }));
  const followUps: AccountTimelineItem[] = detail.followUps.map((followUp) => ({
    id: followUp.id,
    kind: "follow_up",
    occurredAt: followUp.updated_at ?? followUp.created_at,
    title: followUp.action,
    detail: `Due ${formatDateTime(followUp.due_at)}`,
    status: followUp.status,
    channel: "task",
    direction: "outbound",
    actorName: memberName(detail, followUp.assigned_to),
  }));
  const opportunities: AccountTimelineItem[] = detail.opportunities.map((opportunity) => ({
    id: opportunity.id,
    kind: "opportunity",
    occurredAt: opportunity.updated_at,
    title: opportunity.title,
    detail: opportunity.next_step,
    status: opportunity.stage,
    channel: "pipeline",
    direction: "two_way",
    actorName: memberName(detail, opportunity.owner_id),
  }));
  const samples: AccountTimelineItem[] = detail.sampleRequests.map((sample) => ({
    id: sample.id,
    kind: "sample",
    occurredAt: sample.updated_at,
    title: sample.description,
    detail: sample.feedback,
    status: sample.status,
    channel: "commerce",
    direction: "outbound",
    actorName: memberName(detail, sample.owner_id),
  }));
  const quotes: AccountTimelineItem[] = detail.quotes.map((quote) => ({
    id: quote.id,
    kind: "quote",
    occurredAt: quote.updated_at,
    title: quote.summary,
    detail: quote.quote_reference,
    status: quote.status,
    channel: "commerce",
    direction: "outbound",
    actorName: memberName(detail, quote.owner_id),
  }));

  return [...activities, ...orders, ...followUps, ...opportunities, ...samples, ...quotes].sort(
    (first, second) =>
      second.occurredAt.localeCompare(first.occurredAt) || first.id.localeCompare(second.id),
  );
}

export function snoozeUntil(dueAt: string, days: number, now = new Date()): string {
  const due = new Date(dueAt);
  const base = Number.isNaN(due.getTime()) || due < now ? new Date(now) : due;
  base.setDate(base.getDate() + days);
  return base.toISOString();
}

export function isDatabaseConflict(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const candidate = error as { code?: unknown; message?: unknown };
  return (
    candidate.code === "40001" ||
    (typeof candidate.message === "string" &&
      (candidate.message.includes("changed on another device") ||
        candidate.message.includes("Reload before saving")))
  );
}

export function throwDatabaseError(error: { code?: string; message: string } | null): void {
  if (!error) return;
  if (isDatabaseConflict(error)) throw new DatabaseConflictError(error.message);
  throw new Error(error.message);
}

export function withoutUndefined<T extends object>(input: T): Record<string, unknown> {
  return Object.fromEntries(Object.entries(input).filter(([, value]) => value !== undefined));
}

function memberName(detail: VendorDetail, userId: string): string | null {
  return detail.teamMembers.find((member) => member.user_id === userId)?.display_name ?? null;
}
